import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Play } from 'lucide-react';
import { Button } from './ui/Button';
import { useTranslation } from '../i18n';
import { trackEvent } from '../utils/analytics';
const VIDEO_SRC = 'https://cdn.magicpatterns.com/uploads/vDE9Tkj1fGj9bTLpg6TEki/dentico_patient_experience_aligner_review_4x5.mp4';
/** Second of the video shown as the still cover before playback. Adjust to a smiling frame. */
const POSTER_TIME_SECONDS = 1.5;
const WHATSAPP_BASE = 'https://wa.me/447345197820?text=';
const PAGE = 'patient_experience';

/**
 * Patient testimonial video (4:5) with sound on explicit play.
 * No autoplay or loop; native controls (play/pause, seek, volume, fullscreen) appear after starting.
 */
export function PatientReviewVideo() {
  const {
    lang
  } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const pick = (en: string, ua: string, ru: string, ro: string) => lang === 'ua' ? ua : lang === 'ru' ? ru : lang === 'ro' ? ro : en;
  const whatsAppUrl = `${WHATSAPP_BASE}${encodeURIComponent(pick('Hello! I watched the clear aligner video review on your website. I would like to learn more about the treatment and book a consultation. What dates are available?', 'Добрий день! Переглянув/переглянула відеовідгук про елайнери на вашому сайті. Хочу дізнатися більше про лікування та записатися на консультацію. Які є вільні дати?', 'Добрый день! Посмотрел/посмотрела видеоотзыв об элайнерах на вашем сайте. Хочу узнать больше о лечении и записаться на консультацию. Какие есть свободные даты?', 'Bună ziua! Am urmărit videoclipul-recenzie despre alignere de pe site-ul dumneavoastră. Aș dori să aflu mai multe despre tratament și să programez o consultație. Ce date sunt disponibile?'))}`;
  const handleStart = () => {
    const el = videoRef.current;
    if (!el) return;
    setStarted(true);
    el.currentTime = 0;
    el.muted = false;
    el.play().catch(() => undefined);
    trackEvent('video_play', {
      page: PAGE,
      position: 'journey_video'
    });
  };
  const playLabel = pick('Watch with sound', 'Дивитися відгук зі звуком', 'Смотреть отзыв со звуком', 'Vizionați cu sunet');
  const heading = pick('Her clear aligner experience, in her own words', 'Її досвід з елайнерами — її словами', 'Её опыт с элайнерами — её словами', 'Experiența ei cu alignerii, în propriile cuvinte');
  return <div className="w-full max-w-[420px] mx-auto lg:mr-0" data-id="element-610" data-mp="9eu6mepy6ct6">
      <p className="text-sm font-medium text-forest-600 uppercase tracking-widest mb-2" data-id="element-611" data-mp="4g96efap3t4q">
        {pick('Patient story', 'Відгук пацієнтки', 'Отзыв пациентки', 'Povestea unei paciente')}
      </p>
      <h3 className="text-2xl font-medium text-forest-900 mb-5 leading-snug" data-id="element-612" data-mp="1yu0br2lzrqud">
        {heading}
      </h3>

      <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-xl bg-forest-900" data-id="element-613" data-mp="1utq2nh2wkkba">
        <video ref={videoRef} src={`${VIDEO_SRC}#t=${POSTER_TIME_SECONDS}`} preload="metadata" playsInline controls={started} aria-label={heading} className="absolute inset-0 w-full h-full object-contain" data-id="element-614" data-mp="3cqbe0yhwnnjg" />
        {!started && <button type="button" onClick={handleStart} className="group absolute inset-0 flex items-end justify-center pb-8 bg-forest-900/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white" data-id="element-615" data-mp="3qk2lm1c2sxcj">
            <span className="inline-flex items-center gap-2.5 rounded-full bg-white px-4 sm:px-5 min-h-[48px] text-sm sm:text-base font-medium text-forest-900 shadow-lg whitespace-nowrap transition-transform duration-150 ease-out group-hover:scale-[1.03] group-active:scale-[0.98]" data-id="element-616" data-mp="32ui4zp3eac6f">
              <span className="w-8 h-8 rounded-full bg-forest-600 flex items-center justify-center flex-shrink-0" data-id="element-617" data-mp="mzersniz14km">
                <Play className="w-4 h-4 text-white ml-0.5 fill-current" aria-hidden="true" data-id="element-618" data-mp="lm6qfen0z7eg" />
              </span>
              {playLabel}
            </span>
          </button>}
      </div>

      <div className="mt-6 flex flex-col items-stretch gap-2" data-id="element-619" data-mp="uio16grh939o">
        <a href={whatsAppUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('whatsapp_click', {
        page: PAGE,
        position: 'journey_video'
      })} data-id="element-620" data-mp="qidwr8jc2kwx">
          <Button variant="whatsapp" size="lg" className="w-full h-auto min-h-[52px] py-3 px-4 sm:px-6 whitespace-normal leading-snug" data-id="element-621" data-mp="zhr703fa7dip">
            <MessageCircle className="w-5 h-5" aria-hidden="true" data-id="element-622" data-mp="19agg4rnd0fav" />
            {pick('Ask about clear aligners on WhatsApp', 'Запитати про елайнери у WhatsApp', 'Спросить об элайнерах в WhatsApp', 'Întrebați despre alignere pe WhatsApp')}
          </Button>
        </a>
        <Link to={`/${lang}/services/orthodontics/clear-aligners`} className="inline-flex items-center justify-center gap-1.5 min-h-[44px] text-forest-700 font-medium underline-offset-4 hover:underline" data-id="element-623" data-mp="26uzkqwfmcjud">
          {pick('Learn more about clear aligners', 'Дізнатися більше про елайнери', 'Узнать больше об элайнерах', 'Aflați mai multe despre alignere')}
          <ArrowRight className="w-4 h-4" aria-hidden="true" data-id="element-624" data-mp="1nxycn2qrq3m7" />
        </Link>
      </div>
    </div>;
}